import React, {useState} from "react";
import {Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip} from "@mui/material";
import {ScopeType, TargetType} from "../../../../../model/ModelData";
import {IconPlus, IconTrash} from "@tabler/icons-react";
import {v4 as uuid} from "uuid";
import {Editor} from "@monaco-editor/react";
import {ParserModal} from "../../../../../components/modals/ParserModal";
import {UI, rgba} from "../../uiTokens";
import {fieldSx, iconBtnDangerSx, iconBtnSx} from "../editMenuStyles";
import {SubSectionHeader} from "../components/EditMenuSection";

const BodyParamsType = {
    JSON: "application/json",
    URL_ENCODED: "application/x-www-form-urlencoded",
};

const bodyTypeBtnSx = (active) => ({
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    border: `1px solid ${active ? UI.accent : rgba(UI.ink, 0.15)}`,
    color: active ? UI.accent : UI.muted,
    backgroundColor: active ? rgba(UI.accent, 0.08) : "transparent",
    "&:hover": {backgroundColor: rgba(UI.accent, 0.06)},
});

export const HookSettings = ({node, setNode, onUpdate}) => {
    const [expandBodyParams, setExpandBodyParams] = useState(false);

    const onAddScope = () => {
        setNode(prev => {
            const state = {...prev};
            if (!state.data.params) state.data.params = [];
            state.data.params.push({id: uuid(), source: "", scope: ScopeType.FLOW});
            return state;
        });
    };
    const onAddQueryParams = () => {
        setNode(prev => {
            const state = {...prev};
            if (!state.data.queryParams) state.data.queryParams = [];
            state.data.queryParams.push({id: uuid(), key: "", value: ""});
            return state;
        });
    };
    const onAddBodyParams = () => {
        setNode(prev => {
            const state = {...prev};
            if (!state.data.bodyParams) state.data.bodyParams = [];
            state.data.bodyParams.push({id: uuid(), key: "", value: ""});
            return state;
        });
    };

    const isBodyMethod = node?.data?.api_method === "POST" || node?.data?.api_method === "PUT";

    return (
        <>
            <ParserModal
                handleClose={() => setExpandBodyParams(false)}
                open={expandBodyParams}
                onSuccess={(content) => onUpdate({...node, data: {...node?.data, body: content}})}
                text={node?.data?.body || ""}
            />

            <SubSectionHeader title="Hook API"/>
            <Stack direction="row" gap={1}>
                <FormControl size="small" sx={fieldSx}>
                    <InputLabel id="hook-api-method-label">Api Method</InputLabel>
                    <Select
                        labelId="hook-api-method-label"
                        value={node?.data?.api_method || "none"}
                        label="Api Method"
                        onChange={(e) => setNode(prev => ({
                            ...prev,
                            data: {...prev.data, api_method: e.target.value === "none" ? null : e.target.value},
                        }))}
                        onBlur={() => onUpdate(node)}
                    >
                        <MenuItem value="none"><em>None</em></MenuItem>
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="PUT">PUT</MenuItem>
                        <MenuItem value="PATCH">PATCH</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label="Api URL"
                    size="small"
                    value={node?.data?.api_url}
                    InputLabelProps={{shrink: true}}
                    sx={{flex: 1, ...fieldSx}}
                    onChange={(e) => setNode(prev => ({...prev, data: {...prev.data, api_url: e.target.value}}))}
                    onBlur={() => onUpdate(node)}
                />
            </Stack>

            <Stack gap={1}>
                <SubSectionHeader
                    title="Query Params"
                    action={
                        <Tooltip title="Add query param" disableInteractive>
                            <IconButton onClick={onAddQueryParams} size="small" sx={iconBtnSx}>
                                <IconPlus size={16}/>
                            </IconButton>
                        </Tooltip>
                    }
                />
                {node?.data?.queryParams?.map((queryParam, index) => (
                    <Stack key={queryParam?.id} direction="row" gap={0.75} alignItems="center">
                        <TextField
                            size="small" placeholder="Key" sx={{flex: 1, ...fieldSx}}
                            value={queryParam?.key}
                            onBlur={() => onUpdate(node)}
                            onChange={e => setNode(prev => {
                                const state = {...prev};
                                state.data.queryParams[index].key = e.target.value;
                                return state;
                            })}
                        />
                        <TextField
                            size="small" placeholder="Value" sx={{flex: 1, ...fieldSx}}
                            value={queryParam?.value}
                            onBlur={() => onUpdate(node)}
                            onChange={e => setNode(prev => {
                                const state = {...prev};
                                state.data.queryParams[index].value = e.target.value;
                                return state;
                            })}
                        />
                        <IconButton
                            size="small"
                            sx={iconBtnDangerSx}
                            onClick={() => {
                                const state = {...node};
                                if (!state.data.queryParams) state.data.queryParams = [];
                                state.data.queryParams = state.data.queryParams.filter((i, ind) => i.id !== queryParam?.id || ind !== index);
                                onUpdate(state);
                            }}
                        >
                            <IconTrash size={15}/>
                        </IconButton>
                    </Stack>
                ))}
            </Stack>

            <Stack gap={1} sx={{display: isBodyMethod ? "flex" : "none"}}>
                <SubSectionHeader title="Body"/>
                <Stack direction="row" gap={1}>
                    <Button
                        size="small"
                        sx={bodyTypeBtnSx(node?.data?.body_params_type === BodyParamsType.JSON)}
                        onClick={() => onUpdate({...node, data: {...node.data, body_params_type: BodyParamsType.JSON}})}
                    >
                        application/json
                    </Button>
                    <Button
                        size="small"
                        sx={bodyTypeBtnSx(node?.data?.body_params_type === BodyParamsType.URL_ENCODED)}
                        onClick={() => onUpdate({...node, data: {...node.data, body_params_type: BodyParamsType.URL_ENCODED}})}
                    >
                        x-form-urlencoded
                    </Button>
                </Stack>
                {node?.data?.body_params_type === BodyParamsType.JSON ? (
                    <Box
                        onClick={() => setExpandBodyParams(true)}
                        sx={{
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: `1px solid ${rgba(UI.ink, 0.12)}`,
                            cursor: "pointer",
                            "&:hover": {borderColor: rgba(UI.accent, 0.4)},
                        }}
                    >
                        <Editor
                            height="200px"
                            defaultLanguage="javascript"
                            value={node?.data?.body}
                            options={{readOnly: true, minimap: {enabled: false}, fontSize: 12, scrollBeyondLastLine: false}}
                        />
                    </Box>
                ) : (
                    <>
                        {node?.data?.bodyParams?.map((bodyParam, index) => (
                            <Stack key={bodyParam?.id} direction="row" gap={0.75} alignItems="center">
                                <TextField
                                    size="small" placeholder="Key" sx={{flex: 1, ...fieldSx}}
                                    value={bodyParam?.key}
                                    onBlur={() => onUpdate(node)}
                                    onChange={e => setNode(prev => {
                                        const state = {...prev};
                                        state.data.bodyParams[index].key = e.target.value;
                                        return state;
                                    })}
                                />
                                <TextField
                                    size="small" placeholder="Value" sx={{flex: 1, ...fieldSx}}
                                    value={bodyParam?.value}
                                    onBlur={() => onUpdate(node)}
                                    onChange={e => setNode(prev => {
                                        const state = {...prev};
                                        state.data.bodyParams[index].value = e.target.value;
                                        return state;
                                    })}
                                />
                                <IconButton
                                    size="small"
                                    sx={iconBtnDangerSx}
                                    onClick={() => {
                                        const state = {...node};
                                        if (!state.data.bodyParams) state.data.bodyParams = [];
                                        state.data.bodyParams = state.data.bodyParams.filter((i, ind) => i.id !== bodyParam?.id || ind !== index);
                                        onUpdate(state);
                                    }}
                                >
                                    <IconTrash size={15}/>
                                </IconButton>
                            </Stack>
                        ))}
                        <Button
                            onClick={onAddBodyParams}
                            size="small"
                            startIcon={<IconPlus size={15}/>}
                            sx={{alignSelf: "flex-start", textTransform: "none", fontWeight: 600, color: UI.accent, "&:hover": {backgroundColor: rgba(UI.accent, 0.1)}}}
                        >
                            Add Body Param
                        </Button>
                    </>
                )}
            </Stack>

            <SubSectionHeader title="Target"/>
            <FormControl size="small" fullWidth sx={fieldSx}>
                <InputLabel id="hook-target-label">Target</InputLabel>
                <Select
                    labelId="hook-target-label"
                    value={node?.data?.target || null}
                    label="Target"
                    onBlur={() => onUpdate(node)}
                    onChange={(e) => setNode(prev => ({...prev, data: {...prev.data, target: e.target.value}}))}
                >
                    <MenuItem value={null}><em>None</em></MenuItem>
                    <MenuItem value={TargetType.FORM}>Form</MenuItem>
                    <MenuItem value={TargetType.FLOW}>Flow</MenuItem>
                    <MenuItem value={TargetType.GLOBAL}>Global</MenuItem>
                </Select>
            </FormControl>

            <SubSectionHeader
                title="Scope"
                action={
                    <Tooltip title="Add scope" disableInteractive>
                        <IconButton onClick={onAddScope} size="small" sx={iconBtnSx}>
                            <IconPlus size={16}/>
                        </IconButton>
                    </Tooltip>
                }
            />
            {node?.data?.params?.map((param, index) => (
                <Stack key={param?.id || index} direction="row" gap={0.75} alignItems="center">
                    <FormControl size="small" sx={fieldSx}>
                        <InputLabel id={`hook-scope-label-${index}`}>Scope</InputLabel>
                        <Select
                            labelId={`hook-scope-label-${index}`}
                            value={node?.data.params[index].scope || null}
                            label="Scope"
                            onBlur={() => onUpdate(node)}
                            onChange={(e) => setNode(prev => {
                                const state = {...prev};
                                state.data.params[index].scope = e.target.value;
                                return state;
                            })}
                        >
                            <MenuItem value={null}><em>None</em></MenuItem>
                            <MenuItem value={ScopeType.FORM}>Form</MenuItem>
                            <MenuItem value={ScopeType.FLOW}>Flow</MenuItem>
                            <MenuItem value={ScopeType.GLOBAL}>Global</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Source"
                        size="small"
                        value={node.data.params[index].source}
                        sx={{flex: 1, ...fieldSx}}
                        InputLabelProps={{shrink: true}}
                        onBlur={() => onUpdate(node)}
                        onChange={(e) => setNode(prev => {
                            const state = {...prev};
                            if (!state.data.params) state.data.params = [];
                            state.data.params[index].source = e.target.value;
                            return state;
                        })}
                    />
                    <IconButton
                        size="small"
                        sx={iconBtnDangerSx}
                        onClick={() => {
                            const state = {...node};
                            if (!state.data.params) state.data.params = [];
                            state.data.params = state.data.params.filter((i, ind) => i.id !== param?.id || ind !== index);
                            onUpdate(state);
                        }}
                    >
                        <IconTrash size={15}/>
                    </IconButton>
                </Stack>
            ))}
        </>
    );
};
